import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { formatTimestamp } from '@/utils/formatters';
import { base64ChunksToBlob } from '@/utils/file';
import { MessageSender, type Message, type LocalAudioMessage } from '@/types';
import { MessageActions } from './MessageActions';
import { toAbsoluteMediaUrl } from '@/utils/url';
import { UserAudioBubble } from './UserAudioBubble';
import { useMessageLike } from '@/hooks/useMessageLike';
import { useMessageAudio } from '@/hooks/useMessageAudio';
import { useMessageVideo } from '@/hooks/useMessageVideo';
import { WaveformPlayer } from '@/components/audio/WaveformPlayer';
import { VideoMessagePlayer } from './VideoMessagePlayer';
import { EchoAvatar } from '@/components/common/EchoAvatar';
import { useEchoStore } from '@/context/EchoContext';
import { useAudioStore } from '@/store/audioStore';
import { useToast } from '@/context/ToastContext';
import { ResponsiveShareModal } from '@/features/my-echo/components/share/ResponsiveShareModal';
import { InstagramShareModal } from '@/features/my-echo/components/share/InstagramShareModal';
import { FacebookShareModal } from '@/features/my-echo/components/share/FacebookShareModal';
import { ShareCard } from '@/components/share/ShareCard';
import { useShareImage } from '@/hooks/useShareImage';
import {
  openSocialShareUrl,
  buildMessageShareLink,
  buildEchoShareLink,
  SHARE_SOCIALS,
  isMobileDevice,
  tryNativeShareWithImage,
} from '@/utils/share';
import { useHeaderVideo } from '@/context/useHeaderVideo';
import greenMicrophoneIcon from '@/assets/images/svg/green_microphone_icon.svg';

function resolveAudioUrl(msg: {
  audio_url_elevenlabs?: string;
  audio_url_azure?: string;
  audio_url?: string;
}): string | undefined {
  return msg.audio_url_elevenlabs ?? msg.audio_url_azure ?? msg.audio_url;
}

function resolveUserAudioUrl(
  localAudioUrl: string | undefined,
  audioUrl: string | undefined
): string {
  if (localAudioUrl) return localAudioUrl;
  if (!audioUrl) return '';
  return toAbsoluteMediaUrl(audioUrl);
}

function resolveAudioBlobOrUrl(
  isVisualizingAudio: boolean,
  audioUrl: string | undefined,
  finalAudioBuffer: string[] | null | undefined
): string | Blob | null {
  if (!isVisualizingAudio) return null;
  if (finalAudioBuffer?.length) return base64ChunksToBlob(finalAudioBuffer);
  if (audioUrl) return toAbsoluteMediaUrl(audioUrl);
  return null;
}

/** Strip bracket stage directions like [thinks] or [mă gândesc, apoi răspund] from AI text. */
function stripStageDirections(text: string): string {
  return text
    .replace(/\[.*?\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const BUBBLE_ENTER = { opacity: 0, scale: 0.95 };
const BUBBLE_ANIMATE = { opacity: 1, scale: 1 };
const BUBBLE_EXIT = { opacity: 0, scale: 0.95, transition: { duration: 0.15 } };
const BUBBLE_TRANSITION = { duration: 0.2, ease: 'easeOut' as const };

interface VideoBubbleProps {
  videoUrl: string;
  onClose: () => void;
}

const VideoBubble: React.FC<VideoBubbleProps> = ({ videoUrl, onClose }) => (
  <motion.div
    key='video'
    initial={BUBBLE_ENTER}
    animate={BUBBLE_ANIMATE}
    exit={BUBBLE_EXIT}
    transition={BUBBLE_TRANSITION}
    className='flex w-full items-start justify-start'
  >
    <div
      className={cn(
        'relative min-w-[280px] max-w-[420px]',
        'rounded-24',
        'glass-bubble bg-gradient-50',
        'overflow-hidden text-white shadow-md'
      )}
    >
      <VideoMessagePlayer url={videoUrl} onClose={onClose} onEnded={onClose} />
    </div>
  </motion.div>
);

interface WaveformBubbleProps {
  audioBlobOrUrl: string | Blob;
  echoAvatarSrc: string | null | undefined;
  echoVideoAvatarSrc: string | null | undefined;
  onFinish: () => void;
}

const WaveformBubble: React.FC<WaveformBubbleProps> = ({
  audioBlobOrUrl,
  echoAvatarSrc,
  echoVideoAvatarSrc,
  onFinish,
}) => (
  <motion.div
    key='waveform'
    initial={BUBBLE_ENTER}
    animate={BUBBLE_ANIMATE}
    exit={BUBBLE_EXIT}
    transition={BUBBLE_TRANSITION}
    className='flex w-full items-start justify-start'
  >
    <div
      className={cn(
        'relative flex min-w-[300px] max-w-[420px] items-center gap-4',
        'rounded-24',
        'glass-bubble bg-gradient-50',
        'p-3 pr-6 text-white shadow-md'
      )}
    >
      <div className='relative shrink-0'>
        <EchoAvatar src={echoAvatarSrc ?? null} videoSrc={echoVideoAvatarSrc ?? null} size={48} />
        <img
          src={greenMicrophoneIcon}
          alt='Voice Message'
          className='absolute -bottom-1 -right-1 h-[17px] w-[11.77px]'
        />
      </div>
      <div className='min-w-0 flex-1'>
        <WaveformPlayer
          url={audioBlobOrUrl}
          height={30}
          barWidth={3}
          barGap={2}
          barRadius={3}
          waveColor='#FFFFFF'
          progressColor='#6EDF21'
          buttonClassName='text-accent-300 hover:text-white transition-colors'
          className='relative pb-2'
          textClassName='absolute -bottom-1 -right-2 font-sans text-10 font-medium text-white/70'
          onFinish={onFinish}
          autoplay={true}
        />
      </div>
    </div>
  </motion.div>
);

interface TextBubbleProps {
  message: Message;
  isOwn: boolean;
  isSystem: boolean;
  isAiMessage: boolean;
  isTransitioning: boolean;
  isStreamingText: boolean;
  isLoadingAudio: boolean;
  echoInfo?: { id: string; name: string; avatarUrl?: string } | undefined;
}

const TextBubble: React.FC<TextBubbleProps> = ({
  message,
  isOwn,
  isSystem,
  isAiMessage,
  isTransitioning,
  isStreamingText,
  isLoadingAudio,
  echoInfo,
}) => (
  <motion.div
    key='text'
    initial={BUBBLE_ENTER}
    animate={BUBBLE_ANIMATE}
    exit={BUBBLE_EXIT}
    transition={BUBBLE_TRANSITION}
    className={cn(
      'relative w-fit px-6 pb-0 pt-5',
      'glass-bubble',
      isTransitioning ? 'backdrop-blur-none' : 'backdrop-blur-none md:backdrop-blur-md',
      isOwn && [
        'bg-user-bubble-gradient',
        'text-white',
        'rounded-24',
        'max-w-[417px]',
        'w-fit',
        'font-sans text-14 font-medium leading-160 tracking-neg-2 xl:text-18',
      ],
      !isOwn &&
        !isSystem && [
          'bg-gradient-50',
          'text-white',
          'rounded-24',
          'max-w-[636px]',
          'font-sans text-14 font-medium leading-160 tracking-neg-2 xl:text-18',
        ],
      isSystem && 'rounded-2xl bg-accent-200 text-center text-brand-800'
    )}
  >
    {echoInfo && isAiMessage && (
      <p className='mb-1 font-sans text-12 font-bold uppercase tracking-wide text-accent-300'>
        {echoInfo.name}
      </p>
    )}
    <p className='whitespace-pre-wrap break-words'>
      {isAiMessage ? stripStageDirections(message.content) : message.content}
      {isStreamingText && !message.content && (
        <span className='ml-1 animate-pulse text-sm italic opacity-50'>Typing...</span>
      )}
    </p>
    {isLoadingAudio && (
      <div className='mt-3 flex items-center gap-2 rounded-lg bg-black/5 p-2 text-xs opacity-70'>
        Loading audio...
      </div>
    )}
    <p
      className={cn('mt-0 font-sans text-12 font-normal', isSystem ? 'text-center' : 'text-right')}
    >
      {formatTimestamp(message.created_at)}
    </p>
  </motion.div>
);

export interface ChatMessageProps {
  message: Message;
  isOwn?: boolean;
  isTransitioning?: boolean;
  echoId?: string;
  isAudioEnabled?: boolean;
  echoInfo?: { id: string; name: string; avatarUrl?: string };
  hideActions?: boolean;
  videoMessagesEnabled?: boolean;
}

/** Renders a single chat message bubble with streaming text, audio playback, waveform visualization, and rating actions. */
export const ChatMessage = React.forwardRef<HTMLDivElement, ChatMessageProps>(
  (
    {
      message,
      isOwn = message.sender_type === MessageSender.USER,
      isTransitioning = false,
      echoId,
      isAudioEnabled = false,
      echoInfo,
      hideActions = false,
      videoMessagesEnabled = false,
    },
    ref
  ) => {
    const isSystem = message.sender_type === MessageSender.SYSTEM;
    const isAiMessage = message.sender_type === MessageSender.AI;
    const { playVideoInHeader } = useHeaderVideo();

    const isPlaying = useAudioStore(
      (state) => state.activeMessageId === message.id && state.isPlaying
    );
    const stopAudio = useAudioStore((state) => state.stop);

    const effectiveEchoId = echoInfo?.id ?? echoId;
    const persistedMessageId = message.serverMessageId ?? message.id;
    const isTemporaryMessage = message.id.startsWith('temp-');
    const hasPersistedMessage = !isTemporaryMessage || !!message.serverMessageId;
    const {
      rating,
      handleRate,
      handleSubmitFeedback,
      isFeedbackLoading,
      checkIsFirstRatingForEcho,
    } = useMessageLike(persistedMessageId, isAiMessage, effectiveEchoId, message.rating);
    const { allContactEchoes } = useEchoStore();

    const [isVisualizingAudio, setIsVisualizingAudio] = React.useState(false);
    const [isVisualizingVideo, setIsVisualizingVideo] = React.useState(false);
    const [shareModalOpen, setShareModalOpen] = React.useState(false);
    const [igModalOpen, setIgModalOpen] = React.useState(false);
    const [igCopied, setIgCopied] = React.useState(false);
    const [fbModalOpen, setFbModalOpen] = React.useState(false);
    const [fbCopied, setFbCopied] = React.useState(false);
    const [shareImageBlob, setShareImageBlob] = React.useState<Blob | null>(null);
    const [shareCardMounted, setShareCardMounted] = React.useState(false);
    const { addToast } = useToast();
    const shareCardRef = React.useRef<HTMLDivElement>(null);
    const captureShareImage = useShareImage(shareCardRef);

    // Video hook
    const {
      videoStatus,
      videoUrl,
      videoError,
      isGenerating: isVideoGenerating,
      generateVideo,
    } = useMessageVideo(
      persistedMessageId,
      message.video_url,
      message.video_status,
      message.content
    );

    const echoAvatarSrc =
      echoInfo?.avatarUrl ?? (echoId ? allContactEchoes[echoId]?.imageUrl : undefined);
    const echoVideoAvatarSrc = echoId ? allContactEchoes[echoId]?.videoAvatarUrl : undefined;

    const extendedMessage = message as LocalAudioMessage;
    const isUserAudio = isOwn && message.is_audio_message === true;
    const isStreamingText = message.sender_type === MessageSender.AI && !message.isTextComplete;

    const audioUrl = React.useMemo(() => resolveAudioUrl(extendedMessage), [extendedMessage]);

    const { audioBuffer: finalAudioBuffer, isLoading: isLoadingAudio } = useMessageAudio(
      audioUrl,
      message.audioBuffer
    );

    const hasBufferedAudio = !!(finalAudioBuffer && finalAudioBuffer.length > 0);
    const hasAudio = !message.ttsFailed && (!!message.has_audio || hasBufferedAudio);
    // ElevenLabs streams arbitrary transport fragments. Wavesurfer can only
    // decode the assembled MP3 after the stream completes.
    const isAudioPlayable =
      !message.ttsFailed && hasBufferedAudio && message.isAllAudioStreamed === true;

    const audioBlobOrUrl = React.useMemo(
      () => resolveAudioBlobOrUrl(isVisualizingAudio, audioUrl, finalAudioBuffer),
      [isVisualizingAudio, audioUrl, finalAudioBuffer]
    );

    const handlePlay = async () => {
      stopAudio();
      setIsVisualizingVideo(false);
      setIsVisualizingAudio(true);
    };

    const handleCopy = () => {
      navigator.clipboard.writeText(stripStageDirections(message.content));
    };

    const handleVideoClick = () => {
      const canPlay = videoStatus === 'done' && videoUrl;
      const canRetry = videoStatus === 'failed' || videoStatus === 'none';
      if (canPlay) {
        stopAudio();
        setIsVisualizingAudio(false);
        if (videoMessagesEnabled) {
          // Video mode on: play in header instead of inline bubble
          playVideoInHeader(videoUrl);
        } else {
          setIsVisualizingVideo(true);
        }
      } else if (canRetry) {
        void generateVideo();
      }
    };

    React.useEffect(() => {
      if (videoError && videoStatus === 'failed') {
        addToast({ title: 'Video unavailable', subtitle: videoError, type: 'error' });
      }
    }, [videoError, videoStatus, addToast]);

    const echoName = echoInfo?.name ?? (echoId ? allContactEchoes[echoId]?.name : undefined) ?? '';
    const shareLink = buildEchoShareLink(echoName);
    const cleanContent = stripStageDirections(message.content);
    const ogShareLink = buildMessageShareLink(echoName, cleanContent);

    const buildShareText = () =>
      `Check what echo of "${echoName}" said!\n\n"${cleanContent}"\n\nTalk to them here\n${shareLink}`;

    const buildNativeShareText = () =>
      `Check what echo of "${echoName}" said!\n\n"${cleanContent}"\n\nTalk to them here`;

    const mountAndCapture = React.useCallback(async (): Promise<Blob | null> => {
      setShareCardMounted(true);
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      return captureShareImage();
    }, [captureShareImage]);

    const handleShare = async () => {
      if (isMobileDevice() && 'share' in navigator) {
        const imageBlob = await mountAndCapture();
        const shared = await tryNativeShareWithImage(
          buildNativeShareText(),
          shareLink,
          imageBlob,
          `${echoName} on D.ECHO`
        );
        setShareCardMounted(false);
        if (shared) return;
      }
      setShareModalOpen(true);
    };

    const handleSocialShare = async (platform: string) => {
      if (platform === 'instagram' || platform === 'facebook') {
        const imageBlob = await mountAndCapture();
        setShareImageBlob(imageBlob);
        setShareCardMounted(false);
        setShareModalOpen(false);
        if (platform === 'instagram') {
          setIgCopied(false);
          setIgModalOpen(true);
        } else {
          setFbCopied(false);
          setFbModalOpen(true);
        }
        return;
      }

      const rawText = buildShareText();
      const text = encodeURIComponent(rawText);
      const linkForPlatform = platform === 'twitter' ? ogShareLink : shareLink;
      await openSocialShareUrl(platform, text, linkForPlatform, rawText);
      setShareModalOpen(false);
    };

    const handleModalCopy = async (setCopied: (v: boolean) => void) => {
      try {
        await navigator.clipboard.writeText(buildShareText());
        setCopied(true);
      } catch {
        addToast({ title: 'Error', subtitle: 'Failed to copy link.', type: 'error' });
      }
    };

    const handleCopyShareLink = async () => {
      try {
        await navigator.clipboard.writeText(buildShareText());
        addToast({
          title: 'Copied!',
          subtitle: 'Text and link copied to clipboard',
          type: 'success',
        });
      } catch {
        addToast({ title: 'Error', subtitle: 'Failed to copy link.', type: 'error' });
      }
    };

    const actionsHidden = isVisualizingAudio || isVisualizingVideo;

    return (
      <div
        ref={ref}
        className={cn(
          'animate-slide-in mb-6 flex w-full flex-col',
          isOwn ? 'items-end' : 'items-start',
          isSystem && 'items-center'
        )}
      >
        {isUserAudio ? (
          <div className='flex flex-col items-end gap-1'>
            <UserAudioBubble
              audioUrl={resolveUserAudioUrl(extendedMessage.localAudioUrl, message.audio_url)}
            />
          </div>
        ) : (
          <AnimatePresence mode='wait' initial={false}>
            {isVisualizingVideo && videoUrl ? (
              <VideoBubble videoUrl={videoUrl} onClose={() => setIsVisualizingVideo(false)} />
            ) : isVisualizingAudio && audioBlobOrUrl ? (
              <WaveformBubble
                audioBlobOrUrl={audioBlobOrUrl}
                echoAvatarSrc={echoAvatarSrc}
                echoVideoAvatarSrc={echoVideoAvatarSrc}
                onFinish={() => setIsVisualizingAudio(false)}
              />
            ) : (
              <TextBubble
                message={message}
                isOwn={isOwn}
                isSystem={isSystem}
                isAiMessage={isAiMessage}
                isTransitioning={isTransitioning}
                isStreamingText={isStreamingText}
                isLoadingAudio={isLoadingAudio}
                echoInfo={echoInfo}
              />
            )}
          </AnimatePresence>
        )}

        {/* Keep the action row static so audio readiness cannot shift the message layout. */}
        {!hideActions &&
          !isOwn &&
          !isSystem &&
          message.isTextComplete &&
          hasPersistedMessage &&
          !actionsHidden && (
            <div>
              <MessageActions
                onPlay={handlePlay}
                isPlaying={isPlaying}
                onCopy={handleCopy}
                onShare={handleShare}
                rating={rating}
                onRate={handleRate}
                hasAudio={hasAudio}
                isAudioGenerated={isAudioPlayable}
                isAudioEnabled={isAudioEnabled}
                onFeedbackSubmit={handleSubmitFeedback}
                isFeedbackLoading={isFeedbackLoading}
                checkIsFirstRatingForEcho={checkIsFirstRatingForEcho}
                hasVideoSupport={videoMessagesEnabled}
                videoStatus={videoStatus}
                onVideoClick={handleVideoClick}
                isVideoGenerating={isVideoGenerating}
                messageLength={cleanContent.length}
              />
            </div>
          )}

        <ResponsiveShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          socials={SHARE_SOCIALS}
          onShare={handleSocialShare}
          link={buildShareText()}
          onCopy={handleCopyShareLink}
        />

        <InstagramShareModal
          isOpen={igModalOpen}
          onClose={() => {
            setIgModalOpen(false);
            setIgCopied(false);
          }}
          shareText={buildShareText()}
          copied={igCopied}
          onCopy={() => handleModalCopy(setIgCopied)}
          imageBlob={shareImageBlob}
        />

        <FacebookShareModal
          isOpen={fbModalOpen}
          onClose={() => {
            setFbModalOpen(false);
            setFbCopied(false);
          }}
          shareText={buildShareText()}
          copied={fbCopied}
          onCopy={() => handleModalCopy(setFbCopied)}
          shareUrl={ogShareLink}
          imageBlob={shareImageBlob}
        />

        {shareCardMounted && (
          <ShareCard ref={shareCardRef} text={cleanContent} echoName={echoName} />
        )}
      </div>
    );
  }
);

ChatMessage.displayName = 'ChatMessage';
